import {
  getSourceLabel,
  PredicateWithOptionalParam,
  useGetThresholdFromParams,
  useValueWithUnit
} from '@weenat/client/dist/core/alerts/alerts'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import {
  getPredicateKey,
  isAppleScabParams,
  isBased,
  isCumul,
  isForecasted,
  isGDDTMaxed,
  isWaterStatusThresholdParams,
  Predicate
} from '@weenat/client/dist/resources/alerts.types'
import { useIntl } from '@weenat/wintl'
import SelectChip from 'app/src/kit/SelectChip'
import Text from 'app/src/kit/Text'
import isEmpty from 'lodash-es/isEmpty'
import isNil from 'lodash-es/isNil'
import noop from 'lodash-es/noop'
import moment from 'moment-timezone'
import { useMemo } from 'react'
import { styled } from 'styled-components'
import Icons from '../kit/Icons'

const SmallSelectChip = styled(SelectChip)`
  margin-right: 0;
`

interface PredicateBreadcrumbsProps {
  navigateToOp?: () => void
  navigateToParams?: () => void
  navigateToSource?: () => void
  predicate: PredicateWithOptionalParam
}

const Chevron = () => {
  return <Icons.ChevronRight $size='lg' />
}

const PredicateBreadcrumbs: FC<PredicateBreadcrumbsProps> = ({
  navigateToOp,
  navigateToParams,
  navigateToSource,
  predicate
}) => {
  const { t } = useIntl()
  const { source, op, params } = predicate

  const { convertValue } = useConvertedValue()
  const predicateKey = useMemo(
    () => (!isNil(source) && !isNil(op) ? getPredicateKey(predicate as Predicate) : undefined),
    [op, predicate, source]
  )

  const thresholdValue = useGetThresholdFromParams(params)

  const valueWithUnit = useValueWithUnit(
    typeof thresholdValue === 'number' ? thresholdValue : undefined,
    predicate
  )

  return (
    <Flex $mt='md' $alignItems='center'>
      {!isNil(predicate) ? (
        <>
          {!isNil(source) && (
            <SmallSelectChip isSelected={false} onPress={navigateToSource || noop}>
              {getSourceLabel(source)}
            </SmallSelectChip>
          )}
          {!isNil(op) && !isNil(predicateKey) && (
            <>
              <Chevron />
              <SmallSelectChip isSelected={false} onPress={navigateToOp || noop}>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  !isEmpty(t(`alerts.predicateKeys.${predicateKey}.label`))
                    ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      t(`alerts.predicateKeys.${predicateKey}.label`)
                    : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      t(`alerts.operators.${op}.label`)
                }
              </SmallSelectChip>
            </>
          )}
          {!isNil(thresholdValue) && !isNil(params) ? (
            <>
              <Chevron />
              <SmallSelectChip isSelected={false} onPress={navigateToParams || noop}>
                {isForecasted(params) ? (
                  <Text $fontSize='rg' $fontWeight='medium'>
                    {t(
                      'alerts.paramSetups.fieldLabels.amountInHours.optionsLabel',
                      { numberOfHours: thresholdValue },
                      { capitalize: true }
                    )}
                  </Text>
                ) : isAppleScabParams(params) ? (
                  <Text $fontSize='rg' $fontWeight='medium'>
                    {thresholdValue.infectionLevels}
                  </Text>
                ) : isWaterStatusThresholdParams(params) ? (
                  <Flex $gap='md'>
                    <Text $fontSize='rg' $fontWeight='medium'>
                      {thresholdValue.thresholds}
                    </Text>
                    <Text $fontSize='rg' $fontWeight='medium'>
                      {params.aggregateByMetric
                        ? t('models.common.fields.overview_view.label')
                        : thresholdValue.horizons}
                    </Text>
                  </Flex>
                ) : !isCumul(params) ? (
                  <Text $fontSize='rg' $fontWeight='medium'>
                    {valueWithUnit}
                  </Text>
                ) : (
                  <Flex $gap='md'>
                    {isBased(params) ? (
                      <Text $fontSize='rg' $fontWeight='medium'>
                        {`${t('models.alert.fields.base.label', { capitalize: true })} ${convertValue(
                          {
                            value: params.base,
                            metric: 'T'
                          }
                        )}`}
                      </Text>
                    ) : null}
                    {isGDDTMaxed(params) && !isNil(params.gddTMax) ? (
                      <Text $fontSize='rg' $fontWeight='medium'>
                        {`${t('models.alert.fields.threshold.label', { capitalize: true })} ${convertValue(
                          {
                            value: params.gddTMax,
                            metric: 'T'
                          }
                        )}`}
                      </Text>
                    ) : null}
                    <Text $fontSize='rg' $fontWeight='medium'>
                      {!isNil(params.since) ? moment.unix(params.since).format('lll') : ''}
                    </Text>
                    <Text $fontSize='rg' $fontWeight='medium'>
                      {valueWithUnit}
                    </Text>
                    {params.isRepeatable && (
                      <Text $fontSize='rg' $fontWeight='medium'>
                        {t('alerts.paramSetups.fieldLabels.repeatable_abbrev')}
                      </Text>
                    )}
                  </Flex>
                )}
              </SmallSelectChip>
            </>
          ) : null}
        </>
      ) : null}
    </Flex>
  )
}

export default PredicateBreadcrumbs
